import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';

import { useUniqueHtmlId } from '../../../hooks/use-unique-html-id';
import { FIELD_NAMES } from '../constants';
import { useAddressField, useAddressFormContext } from '../context';
import { commonMessages, subLabelMessages } from '../messages';

export const AddressInput = ({ field, optional = false, ...rest }) => {
  const intl = useIntl();
  const [touched, setTouched] = useState(false);
  const { showAllErrors, disabled, disableEmail, hideOptionalLabels } = useAddressFormContext();
  const id = useUniqueHtmlId(field);

  const {
    value,
    onChange,
    onBlur,
    error,
    labelDescriptor,
    placeholderDescriptor,
    externallyRequired,
    hideField,
    trackInputClick,
    subLabel,
    isRequired,
  } = useAddressField(field);

  const _onClick = useCallback(() => trackInputClick(field), [field, trackInputClick]);

  const _onChange = useCallback(
    (event) => {
      onChange(event?.target?.value);
    },
    [onChange],
  );

  const _onBlur = useCallback(
    (event) => {
      onBlur(event?.target?.value);
      setTouched(true);
    },
    [onBlur, setTouched],
  );

  const nameToRecurlyMap = {
    line1: 'address1',
    line2: 'address2',
    postalCode: 'postal_code',
    city: 'city',
    state: 'state',
    phone: 'phone',
  };
  const subLabelToRender = subLabel ? intl.formatMessage(subLabelMessages[subLabel]) : null;

  const shouldShowErrors = touched || showAllErrors;
  const errorMessage = shouldShowErrors && error?.message;
  const inlineMessage = errorMessage || subLabelToRender;
  const inlineMessageTooltip = shouldShowErrors && error?.tooltip;
  const inlineMessageType = errorMessage ? INLINE_MESSAGE_TYPES.error : INLINE_MESSAGE_TYPES.sublabel;

  // Create label with red asterisk for required fields
  const baseLabel = labelDescriptor && intl.formatMessage(labelDescriptor);
  const label = isRequired && baseLabel ? <LabelWithRequiredIndicator>{baseLabel}</LabelWithRequiredIndicator> : baseLabel;

  // Show the "(optional)" label for fields that are:
  // 1. Not required based on country rules
  // 2. Not marked as externally required by a consumer
  // 3. Marked as optional via props
  // 4. Not hidden by hideOptionalLabels prop
  const shouldShowOptionalFieldLabel = !isRequired && !externallyRequired && optional && !hideOptionalLabels;
  const additionalInfoLabel = shouldShowOptionalFieldLabel ? intl.formatMessage(commonMessages.optionalFieldSubLabel) : null;
  const placeholder = placeholderDescriptor && intl.formatMessage(placeholderDescriptor);

  return (
    !hideField && (
      <Input
        additionalInfoLabel={additionalInfoLabel}
        data-private
        data-recurly={nameToRecurlyMap[field]}
        disabled={disabled || (field === FIELD_NAMES.EMAIL && disableEmail)}
        id={id}
        inlineMessage={inlineMessage}
        inlineMessageTooltip={inlineMessageTooltip}
        inlineMessageType={inlineMessageType}
        label={label}
        name={field}
        onBlur={_onBlur}
        onChange={_onChange}
        onClick={_onClick}
        placeholder={placeholder}
        value={value}
        version={version2}
        {...rest}
      />
    )
  );
};

AddressInput.propTypes = {
  field: PropTypes.oneOf(Object.values(FIELD_NAMES)).isRequired,
  optional: PropTypes.bool,
};
